import { useState, useEffect, useMemo, useRef } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import JiraSoftwareNav from '@/templates/software/jira/sections/nav-bar/software-jira-navbar';

import { experiments } from '../../../utils/experiment/manifest';
import { EnrollmentType, useExperiment } from '../../../utils/experiment/use-experiment';
import { useExperimentExposure } from '../../../utils/experiment/use-experiment-exposure';
import { PaidJiraAiBody } from '../sections/cfind-4666-frictionless-first-look/paid-jira-ai-body';
import { PaidJiraAiDisclaimer } from '../sections/cfind-4666-frictionless-first-look/paid-jira-ai-disclaimer';
import { PaidJiraAiTeamTypeMadLibPrompt } from '../sections/cfind-4666-frictionless-first-look/paid-jira-ai-team-type-mad-lib-prompt';
import {
	getPromptSummary,
	InlinePreview,
	shouldShowInlinePreview,
} from '../sections/cfind-6215-frictionless-first-look-jira-v2/inline-preview';
import { jiraAiV2PromptHeadingStyles } from '../sections/cfind-6215-frictionless-first-look-jira-v2/jira-ai-v2-prompt-heading-styles';
import { messages as jiraAiV2Messages } from '../sections/cfind-6215-frictionless-first-look-jira-v2/messages';
import { paidJiraAiBackgroundV2SlideMs } from '../sections/cfind-6215-frictionless-first-look-jira-v2/paid-jira-ai-background-v2';
import { PaidJiraAiHeaderV2 } from '../sections/cfind-6215-frictionless-first-look-jira-v2/paid-jira-ai-header-v2';

import { JiraAiFFLProvider, useJiraAiFFL } from './hooks/use-jira-ai-ffl-context';

const promptContainerCss = css({
	marginBlockStart: '24px',
	width: '100%',
	maxWidth: '1152px',
	marginInline: 'auto',
	boxSizing: 'border-box',
	paddingInline: '24px',
	position: 'relative',
	textAlign: 'center',
	isolation: 'isolate',
});

const promptShellExitCss = css({
	opacity: 0,
	transform: 'translateY(-12px)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
	pointerEvents: 'none',
});

const promptContentCss = css({
	position: 'relative',
	zIndex: 1,
	width: '100%',
	maxWidth: '100%',
	marginInline: 'auto',
	opacity: 1,
	transform: 'translateY(0)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
});

const promptHeadingCss = css(jiraAiV2PromptHeadingStyles);

const promptContentExitCss = css({
	opacity: 0,
	transform: 'translateY(-10px)',
	pointerEvents: 'none',
});

const disclaimerExitCss = css({
	opacity: 0,
	transform: 'translateY(-8px)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
	pointerEvents: 'none',
});

export const JiraProductTourAiTemplateContent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const promptRef = useRef<HTMLDivElement>(null);
	const {
		cgsDataLoading,
		isErrorFromCgs,
		hasSubmittedDefaultValues,
		formattedCgsData,
		rawCgsData,
		workingOnValue,
		goalValue,
		workingOnSuggestion,
		goalSuggestion,
	} = useJiraAiFFL();
	const [jiraFflV2ExposureFired, fireJiraFflV2Exposure] = useExperimentExposure(
		experiments.cfind6469FrictionlessFirstLookJiraV3,
	);
	const jiraFflV2Experiment = useExperiment(experiments.cfind6469FrictionlessFirstLookJiraV3);
	const [hideOriginalPrompt, setHideOriginalPrompt] = useState(cgsDataLoading);
	const [isPromptExiting, setIsPromptExiting] = useState(false);

	const promptSummary = useMemo(
		() =>
			getPromptSummary({
				workingOnValue,
				workingOnSuggestion,
				goalValue,
				goalSuggestion,
			}),
		[goalSuggestion, goalValue, workingOnSuggestion, workingOnValue],
	);

	const showInlinePreview =
		hideOriginalPrompt &&
		!isPromptExiting &&
		shouldShowInlinePreview({
			cgsDataLoading,
			formattedCgsData,
		});

	useEffect(() => {
		if (jiraFflV2Experiment.type === EnrollmentType.ENROLLED && !jiraFflV2ExposureFired) {
			fireJiraFflV2Exposure();
		}
	}, [jiraFflV2Experiment.type, jiraFflV2ExposureFired, fireJiraFflV2Exposure]);

	useEffect(() => {
		if (!cgsDataLoading || hideOriginalPrompt) {
			return;
		}

		setIsPromptExiting(true);
		setHideOriginalPrompt(true);
		window.setTimeout(() => {
			setIsPromptExiting(false);
		}, paidJiraAiBackgroundV2SlideMs);
	}, [cgsDataLoading, hideOriginalPrompt]);

	useEffect(() => {
		if (!analyticsClient) {
			return; // be sure analyticsClient is available before firing event
		}
		createAnalyticsEvent({
			action: 'viewed',
			eventType: 'screen',
			name: 'PaidLandingJiraAiTemplate',
			attributes: {
				flow: 'paidLandingJiraAi',
				variant: 'v2',
			},
		}).fire();
	}, [analyticsClient, createAnalyticsEvent]);

	return (
		<main>
			<JiraSoftwareNav />
			<PaidJiraAiHeaderV2 />
			{(!hideOriginalPrompt || isPromptExiting) && (
				<>
					<div css={[promptContainerCss, isPromptExiting && promptShellExitCss]}>
						{(!hideOriginalPrompt || isPromptExiting) && (
							<div
								css={[promptContentCss, isPromptExiting && promptContentExitCss]}
								ref={promptRef}
							>
								<h2 css={promptHeadingCss}>
									<FormattedMessage {...jiraAiV2Messages.promptBoxHeading} />
								</h2>
								<PaidJiraAiTeamTypeMadLibPrompt ctaMessage={jiraAiV2Messages.tryItOut} />
							</div>
						)}
					</div>
					{(!hideOriginalPrompt || isPromptExiting) && (
						<div css={[isPromptExiting && disclaimerExitCss]}>
							<PaidJiraAiDisclaimer />
						</div>
					)}
				</>
			)}
			{showInlinePreview && (
				<InlinePreview
					cgsDataLoading={cgsDataLoading}
					isErrorFromCgs={isErrorFromCgs}
					hasSubmittedDefaultValues={hasSubmittedDefaultValues}
					promptSummary={promptSummary}
					formattedCgsData={formattedCgsData}
					spaceName={rawCgsData?.sampleIssues.spaceName}
					onTryAnotherPrompt={() => {
						setIsPromptExiting(false);
						setHideOriginalPrompt(false);
						window.requestAnimationFrame(() => {
							promptRef.current?.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						});
					}}
				/>
			)}
			<PaidJiraAiBody />
		</main>
	);
};

export const JiraProductTourAiTemplate = () => (
	<JiraAiFFLProvider>
		<JiraProductTourAiTemplateContent />
	</JiraAiFFLProvider>
);
